<template>
  <ApolloMutation
    :mutation="addBookListToCartMutation"
    :variables="{
      itemIds: booksToAdd,
      orderIri: $store.state.cart.currentCart,
      locale: countryCode,
      bookListId: bookListId,
    }"
    @done="onDone"
  >
    <template #default="{ loading, mutate }">
      <v-btn
        tile
        elevation="0"
        color="primary"
        class="px-2 px-sm-3"
        :small="$vuetify.breakpoint.xs"
        :block="block"
        :disabled="$apollo.loading || !!loading"
        :loading="$apollo.loading || !!loading"
        @click="handleAddToCart(mutate)"
      >
        {{ booksToAdd.length ? $t("bookList.addListToCart") : $t("shop.viewCart") }}
        <v-icon
          v-if="booksToAdd.length"
          class="ml-2"
          :size="$vuetify.breakpoint.xs ? 14 : 18"
        >
          $basket
        </v-icon>
      </v-btn>
      <success-snackbar
        v-model="snackbar"
        :message="$t('shop.addedToCart')"
      />
    </template>
  </ApolloMutation>
</template>

<script>
import OrderQueries from '@/graphql/OrderQueries';
import BookQueries from '@/graphql/BookQueries';
import SuccessSnackbar from '@/components/SuccessSnackbar';
import { mapActions } from 'vuex';

export default {
  name: "AddBookListToCartButton",
  components: {
    SuccessSnackbar,
  },
  props: {
    bookList: {
      type: Array,
      default: () => [],
    },
    bookListId: {
      type: String,
      default: null,
    },
    block: {
      type: Boolean,
      default: false,
    },
    // Optional async hook awaited before the cart mutation fires; lets the
    // owner page flush debounced quantity edits so the server-side list is
    // current when the API mirrors it into the cart.
    beforeMutate: {
      type: Function,
      default: null,
    },
    openCartOnDone: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      snackbar: false,
      itemsInCart: [],
      itemsInBookshelf: [],
    };
  },
  computed: {
    booksToAdd() {
      return this.bookList.filter((book) => (
        !this.itemsInCart.includes(book) && !this.itemsInBookshelf.includes(book)
      ));
    },
    addBookListToCartMutation() {
      return OrderQueries.addMultipleItemsToOrder;
    },
    currentCart() {
      return this.$store.state.cart.currentCart;
    },
    countryCode() {
      return this.$store.state.locale.countryCode;
    },
    isLoggedIn() {
      return this.$auth.isAuthenticated;
    },
  },
  apollo: {
    order: {
      query: OrderQueries.getOrder,
      variables() {
        return {
          orderId: this.currentCart,
        };
      },
      skip() {
        return !this.currentCart;
      },
      update(data) {
        if (data) {
          this.itemsInCart = data.order.orderLines.edges.map((edge) => edge.node.productId);
        }
      },
    },
    books: {
      query: BookQueries.getUserBookshelf,
      skip() {
        return !this.isLoggedIn;
      },
      update(data) {
        if (data) {
          this.itemsInBookshelf = data.books.collection.map((item) => item._id);
        }
      },
    },
  },
  methods: {
    ...mapActions('siteFunctionality', ['changeShowCartDrawer']),
    async handleAddToCart(mutate) {
      if (this.booksToAdd.length) {
        if (this.beforeMutate) {
          await this.beforeMutate();
        }
        mutate();
      } else {
        this.changeShowCartDrawer(true);
      }
    },
    async onDone({ data }) {
      const order = data?.addMultipleItemsToOrder?.order;
      if (!order) return;

      if (order.id !== this.currentCart) {
        this.$store.commit("setCurrentCart", order.id);
      }

      this.itemsInCart = order.orderLines.edges.map((edge) => edge.node.productId);
      this.$store.commit('markCartDataStale');
      const refresh = this.$store.dispatch('fetchCartData', {
        apolloClient: this.$apollo.provider.defaultClient,
        forceNetwork: true,
      }).catch(() => {});
      this.$apollo.queries.order?.refetch?.().catch(() => {});

      if (this.openCartOnDone) {
        this.changeShowCartDrawer(true);
      } else {
        this.snackbar = true;
      }

      this.$emit('cartUpdated', order);
      await refresh;
    }
  },
};
</script>